/** Checklists and choices for case files, applicants, FOIA, body cam reviews, events and SRO safety concerns. */

// ─── Case file assembly (the folder's printed checklist, in order) ───────────
export const CASE_FILE_ITEMS = [
  { key: 'questionnaire', label: 'Officer Questionnaire', auto: null },
  { key: 'summary', label: 'Case Summary', auto: 'summary' },
  { key: 'report', label: 'Incident Report / Supplements', auto: 'report' },
  { key: 'victim', label: 'Victim Report', auto: null },
  { key: 'warrant', label: 'Warrant / Ticket', auto: 'warrants' },
  { key: 'rights', label: 'Rights / Statements', auto: 'rights' },
  { key: 'ba', label: 'B/A Report / Refusal Forms', auto: null },
  { key: 'criminal', label: 'Criminal Record', auto: null },
  { key: 'driving', label: 'Drivers Record Ten Years', auto: null },
  { key: 'lab', label: 'Lab Analysis / Reports', auto: null },
  { key: 'evidence', label: 'Evidence List / Log', auto: 'evidence' },
  { key: 'autopsy', label: 'Autopsy Report', auto: null },
  { key: 'photos', label: 'Photographs', auto: 'photos' },
  { key: 'other', label: 'Other', auto: null },
] as const;
export type CaseFileItemKey = (typeof CASE_FILE_ITEMS)[number]['key'];
export type CaseFileItemState = { state: 'done' | 'na'; note?: string | null; by?: string; byName?: string; at?: string };
export const CASE_FILE_REASON_LABEL = { felony: 'Felony (General Sessions)', general_sessions: 'General Sessions charge', domestic_violence: 'Domestic violence' } as const;

/** Every item done or not applicable, and a case summary written. */
export function caseFileMissing(checklist: Record<string, CaseFileItemState | undefined>, summary: string | null | undefined): string[] {
  const out: string[] = [];
  for (const it of CASE_FILE_ITEMS) if (!checklist[it.key]) out.push(it.label);
  if (checklist.summary?.state === 'done' && !summary?.trim()) out.push('Case Summary (write it)');
  return out;
}

// ─── Applicants (New Hire Checklist + steps added by the command staff) ──────
export const APPLICANT_STEPS = [
  { key: 'received', label: 'Received application', who: 'hr' },
  { key: 'complete', label: 'All documents received — application complete', who: 'hr' },
  { key: 'contacted', label: 'Candidate contacted', who: 'hr' },
  { key: 'dmv_ncic', label: 'DMV and NCIC checked (E-911)', who: 'dispatch' },
  { key: 'acadis', label: 'ACADIS BG form sent / received', who: 'hr' },
  { key: 'interview', label: 'Initial interview scheduled', who: 'hr' },
  { key: 'interviewed', label: 'Interview complete', who: 'hr' },
  { key: 'test_day', label: 'Test day scheduled', who: 'hr' },
  { key: 'employer', label: 'Employer contacted', who: 'hr' },
] as const;
export type ApplicantStepKey = (typeof APPLICANT_STEPS)[number]['key'];
export type ApplicantStep = { date?: string | null; at?: string | null; note?: string | null; by?: string; byName?: string; sentOn?: string | null; receivedFrom?: string | null; receivedOn?: string | null };
export const APPLICANT_DIVISIONS = [
  { code: 'sheriff', label: 'Sheriff’s Office' },
  { code: 'detention', label: 'Detention' },
] as const;
export const APPLICANT_RESULTS = [
  { code: 'hired', label: 'Hired' },
  { code: 'not_selected', label: 'Not selected' },
  { code: 'withdrew', label: 'Withdrew' },
] as const;

/** A step is done when its date is in (ACADIS: when the email came back). */
export const applicantStepDone = (key: string, s: ApplicantStep | undefined) => !!s && (key === 'acadis' ? !!s.receivedOn : !!(s.date || s.at));
export function applicantNextStep(steps: Record<string, ApplicantStep | undefined>) {
  return APPLICANT_STEPS.find((s) => !applicantStepDone(s.key, steps[s.key])) ?? null;
}

// ─── FOIA (the office's 7-step process) ───────────────────────────────────────
export const FOIA_STEPS = [
  { n: 1, label: 'Intake and create determination letter', who: 'Katie' },
  { n: 2, label: 'Gather and redact requested information', who: 'FOIA Coordinator' },
  { n: 3, label: 'Review redactions', who: 'Katie' },
  { n: 4, label: 'Prepare requested information', who: 'FOIA Coordinator' },
  { n: 5, label: 'Complete closing letter and ensure all material is present', who: 'Londa' },
  { n: 6, label: 'Sign closing letter', who: 'Katie' },
  { n: 7, label: 'Send out and close file', who: 'FOIA Coordinator' },
] as const;
export type FoiaStep = { n: number; personId: string | null; doneOn: string | null; note?: string | null };

/** Fees: hours × hourly rate + pages × per-page rate + anything else. Blank rates → no total yet. */
export function foiaFee(x: { hours?: number | null; pages?: number | null; hourlyRate?: number | null; pageRate?: number | null; otherFees?: number | null }) {
  const labor = x.hours != null && x.hourlyRate != null ? x.hours * x.hourlyRate : null;
  const copies = x.pages != null && x.pageRate != null ? x.pages * x.pageRate : null;
  const parts = [labor, copies, x.otherFees ?? null];
  const known = parts.filter((p): p is number => p != null);
  return { labor, copies, total: known.length ? Math.round(known.reduce((a, b) => a + b, 0) * 100) / 100 : null, missingRates: (x.hours != null && x.hourlyRate == null) || (x.pages != null && x.pageRate == null) };
}

// ─── Body-worn camera supervisor review (the office's Jotform) ───────────────
export const BWC_RANKS = [
  'Major',
  'Captain-Enforcement',
  'Captain-Special Services',
  'Director-Detention',
  'Lieutenant-Enforcement',
  'Lieutenant-Special Services',
  'Lieutenant-Detention',
  'Sergeant-Enforcement',
  'Sergeant-Special Services',
  'Sergeant-Detention',
  'Corporal-Enforcement',
  'Corporal-Special Services',
  'Corporal-Detention',
] as const;
export const BWC_ASSIGNMENTS = ['Enforcement', 'Special Services', 'Detention'] as const;
export const BWC_INCIDENT_TYPES = ['Traffic Stop', 'Call for Service', 'Arrest', 'Field Interview', 'Vehicle Pursuit', 'Use of Force', 'Search', 'Warrant Service', 'Other'] as const;
export const BWC_REASONS = ['Late Activation', 'Early Deactivation', 'Policy Violation', 'Safety Issue', 'Other'] as const;
export const BWC_ACTIONS = [
  'Verbal Counseling',
  'Training Reminder',
  'Documented Counseling',
  'Written Warning',
  'Referral for formal training',
  'Referred to Internal Affairs / Professional Standards',
  'No Action Required',
  'Other',
] as const;
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

// ─── SRO safety concerns ──────────────────────────────────────────────────────
export const SAFETY_PLACES = ['Classroom', 'Hallway', 'Building / wing', 'Entrance / door', 'Stairwell', 'Restroom', 'Cafeteria', 'Gym / field', 'Parking lot', 'Bus loop', 'Outside / grounds', 'Other'] as const;
export const SAFETY_STATUS_LABEL = { open: 'Open', reported: 'Reported to the school', fixed: 'Fixed' } as const;

// ─── Outside contacts (people outside BlueHQ who shut things off) ─────────────
export const OUTSIDE_ROLES = [
  { code: 'school_access', label: 'School district — building access cards' },
  { code: 'county_access', label: 'County — building access cards' },
  { code: 'rfid', label: 'RFID tags (vehicles)' },
  { code: 'county_fleet', label: 'County — vehicle insurance and fuel cards' },
  { code: 'e911', label: 'E-911 center — car assignment changes' },
] as const;
export const SHUTOFF_LABEL = { school_access: 'School building access', county_access: 'County building access', rfid: 'RFID tag', insurance: 'Insurance', fuel_card: 'Fuel card' } as const;

// ─── Where property came from / where its money goes (stays with the item for its whole life) ─────────────
export const FUNDING = {
  narcotics: { label: 'Narcotics seizure', short: 'Narcotics', proceeds: 'Money from selling it goes to the narcotics (drug forfeiture) fund — not the general fund.', ref: 'Seizure case #' },
  victim_advocate: { label: 'Victim’s advocate', short: 'Victim advocate', proceeds: 'Bought with victim’s advocate money — it stays with the program, and sale money goes back to it.', ref: 'Program / account' },
  grant: { label: 'Grant', short: 'Grant', proceeds: 'Bought with a grant — check the grant’s rules before selling; sale money may have to go back to the grant.', ref: 'Grant #' },
} as const;
export type Funding = keyof typeof FUNDING;
